import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { PaymentMethod } from '../types/models/PaymentMethod';
import type { Wallet, WalletDeposit } from '../types/models/Wallet';
import type { ApiResponseWalletShow } from '../types/api/ApiResponseWallet';

export interface UseWalletReturn {
  /** Balance + top-up bounds, or `null` until loaded / on error. */
  wallet: Wallet | null;
  /** Top-ups still awaiting a Mollie outcome. */
  pendingDeposits: WalletDeposit[];
  /** The default saved card, or `null` when none. */
  defaultPaymentMethod: PaymentMethod | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /clients/wallet` — the tenant's prepaid balance, its pending top-ups and the default saved
 * card (docs/api-types/wallet.md). Impersonation-safe: reads the in-scope client.
 *
 * `enabled` defers the fetch (e.g. until a modal opens or a lazily-mounted tab is entered) so the
 * request never fires for users who don't reach the surface that needs it. While disabled the hook
 * reports `loading: true` only once enabled, so a consumer gating on `!loading && wallet` renders
 * nothing wallet-related before the first fetch.
 */
export const useWallet = (enabled: boolean = true): UseWalletReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [pendingDeposits, setPendingDeposits] = useState<WalletDeposit[]>([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/wallet');
      // JSend: branch on body.status, never response.ok (errors arrive at HTTP 200).
      const body = (await response.json().catch(() => null)) as ApiResponseWalletShow | null;
      if (body?.status === 'success' && body.data?.wallet) {
        setWallet(body.data.wallet);
        setPendingDeposits(body.data.pending_deposits ?? []);
        setDefaultPaymentMethod(body.data.default_payment_method ?? null);
        return;
      }
      setError(body?.message ?? 'Could not load your prepaid balance.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading your prepaid balance.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchWallet();
    }
  }, [enabled, auth.isAuthenticated, fetchWallet]);

  return { wallet, pendingDeposits, defaultPaymentMethod, loading, error, refetch: fetchWallet };
};

export default useWallet;
